/* apercu-prenom.js — Aperçu navigateur du prénom sur un design (option A, CDC v2 D6)
 * Générique : tout vient du manifeste du design (poc/apercu/<templateId>/manifest.json),
 * produit automatiquement depuis le maître Illustrator. Aucune valeur propre à un design ici.
 * Reproduit les règles du moteur de production : normalisation, validation, réduction
 * proportionnelle jusqu'au plancher, centrage sur la position du designer, habillage
 * (dégradé, contour, ombre). Rendu indicatif : la production reste faite par Illustrator.
 */
(function (global) {
  "use strict";

  var cache = {};

  function normaliser(brut, regles) {
    var n = String(brut || "").trim().replace(/\s+/g, " ").replace(/[’‘]/g, "'");
    if (regles.textCase === "uppercase") n = n.toUpperCase();
    else if (regles.textCase === "lowercase") n = n.toLowerCase();
    return n;
  }

  function valider(n, regles) {
    if (!n) return "Saisissez un prénom.";
    if (n.length > (regles.maxCharacters || 20)) return "Le prénom dépasse " + (regles.maxCharacters || 20) + " caractères.";
    var extra = (regles.allowSpaces !== false ? " " : "") + (regles.allowHyphens !== false ? "\\-" : "") + (regles.allowApostrophes !== false ? "'" : "");
    var re = new RegExp("^[\\p{L}" + extra + "]+$", "u");
    if (!re.test(n)) return "Lettres, accents, espaces, tirets et apostrophes uniquement.";
    if (/^[\s'\-]|[\s'\-]$/.test(n)) return "Le prénom ne peut pas commencer ou finir par un espace, un tiret ou une apostrophe.";
    return null;
  }

  function chargerImage(url) {
    return new Promise(function (res, rej) {
      var im = new Image();
      im.onload = function () { res(im); };
      im.onerror = function () { rej(new Error("image introuvable : " + url)); };
      im.src = url;
    });
  }

  /* Charge manifeste + fond + police depuis des URL explicites (Shopify : assets du thème). */
  function chargerDepuis(u) {
    var cle = u.templateId || u.manifestUrl;
    if (cache[cle]) return cache[cle];
    cache[cle] = (async function () {
      var m = await (await fetch(u.manifestUrl, { cache: "no-store" })).json();
      var fonts = await (await fetch(u.fontsUrl, { cache: "no-store" })).json();
      var fichier = fonts[m.font.postscript] || fonts[m.font.family];
      if (!fichier) throw new Error("Police non fournie pour l'aperçu : " + m.font.postscript);
      var famille = "apercu-" + m.templateId;
      var face = new FontFace(famille, "url(" + u.fontDir + fichier + ")");
      await face.load();
      document.fonts.add(face);
      var fond = await chargerImage(u.fondUrl);
      return { manifest: m, famille: famille, fond: fond };
    })();
    return cache[cle];
  }

  /* Charge un design depuis le dossier apercu local : base/<templateId>/manifest.json, fond.png, fonts/. */
  function charger(base, templateId) {
    return chargerDepuis({
      templateId: templateId,
      manifestUrl: base + "/" + templateId + "/manifest.json",
      fondUrl: base + "/" + templateId + "/fond.png",
      fontsUrl: base + "/fonts/fonts.json",
      fontDir: base + "/fonts/"
    });
  }

  function rgb(a) { return "rgb(" + a[0] + "," + a[1] + "," + a[2] + ")"; }

  function mesurer(ctx, texte) {
    var met = ctx.measureText(texte);
    return {
      largeur: met.actualBoundingBoxLeft + met.actualBoundingBoxRight,
      gauche: met.actualBoundingBoxLeft,
      montee: met.actualBoundingBoxAscent,
      descente: met.actualBoundingBoxDescent,
      hauteur: met.actualBoundingBoxAscent + met.actualBoundingBoxDescent
    };
  }

  /* Dessine le design + le prénom sur le canvas. Rend un compte rendu (taille, réduction). */
  function dessiner(canvas, design, prenomNormalise) {
    var m = design.manifest, s = m.style || {}, t = m.text, z = m.zone;
    canvas.width = m.width; canvas.height = m.height;
    var ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, m.width, m.height);
    ctx.drawImage(design.fond, 0, 0, m.width, m.height);
    if (!prenomNormalise) return { vide: true };

    var police = function (taille) { return taille + "px \"" + design.famille + "\""; };
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";

    // position du designer, lue dans le maître : boîte exacte des glyphes (tight*) et boîte de ligne (box*)
    ctx.font = police(t.size);
    var ref = mesurer(ctx, t.reference);
    var tLeft = (t.tightLeft != null) ? t.tightLeft : t.boxLeft, tRight = (t.tightRight != null) ? t.tightRight : t.boxRight;
    var tTop = (t.tightTop != null) ? t.tightTop : t.boxTop;
    var centreX = (tLeft + tRight) / 2;
    var ligneBase = tTop + ref.montee;                    // ligne de base exacte du maître
    var boxH = t.boxBottom - t.boxTop;                    // hauteur de la boîte de ligne Illustrator
    var ascLigne = ligneBase - t.boxTop;                  // ascendante de ligne (Illustrator) à la taille de référence
    var centreLigneY = (t.boxTop + t.boxBottom) / 2;

    // ajustement proportionnel (même logique que le moteur : largeur des glyphes et hauteur de ligne
    // comparées à la zone ; réduction seule ; plancher ; facteur 0.985)
    var zoneW = z.right - z.left, zoneH = z.bottom - z.top;
    var taille = t.size, min = t.size * (m.rules.minimumFontSizePercent || 40) / 100, reduit = false, iter = 0;
    var mes = mesurer(ctx, prenomNormalise);
    while (iter < 10) {
      var k0 = taille / t.size;
      var ratio = Math.min(zoneW / mes.largeur, zoneH / (boxH * k0));
      if (ratio >= 1 || taille <= min + 0.01) break;
      taille = Math.max(min, taille * ratio * 0.985);
      ctx.font = police(taille);
      mes = mesurer(ctx, prenomNormalise);
      reduit = true; iter++;
    }
    var facteur = taille / t.size;
    var tient = mes.largeur <= zoneW + 0.5 && (boxH * facteur) <= zoneH + 0.5;

    // placement : centré horizontalement sur le centre d'origine des glyphes ; vertical = ligne de base
    // d'origine, ou boîte de ligne recentrée sur son centre d'origine si réduit (comme le moteur)
    var x = centreX - mes.largeur / 2 + mes.gauche;
    var y = reduit ? (centreLigneY - (boxH * facteur) / 2 + ascLigne * facteur) : ligneBase;

    // habillage
    var haut = y - mes.montee, bas = y + mes.descente;
    var fill;
    if (s.mode === "rich" && s.gradientStops && s.gradientStops.length > 1) {
      fill = ctx.createLinearGradient(0, haut, 0, bas);
      s.gradientStops.forEach(function (st) { fill.addColorStop(Math.min(1, Math.max(0, st[0] / 100)), "rgb(" + st[1] + "," + st[2] + "," + st[3] + ")"); });
    } else {
      fill = rgb(s.fill || [0, 0, 0]);
    }
    var contour = s.mode === "rich" && s.hasStroke && (s.strokeWeight || 0) > 0;
    var epaisseur = (s.strokeWeight || 0) * facteur;  // le moteur réduit le contour du même facteur

    function tracer() {
      if (contour) { ctx.lineWidth = epaisseur; ctx.lineJoin = "miter"; ctx.miterLimit = 10; ctx.strokeStyle = rgb(s.strokeRGB); ctx.strokeText(prenomNormalise, x, y); }
      ctx.fillStyle = fill; ctx.fillText(prenomNormalise, x, y);
    }
    ctx.save();
    if (s.shadow) {  // passe 1 : ombre portée de l'ensemble contour + fond
      ctx.shadowColor = "rgba(0,0,0," + (s.shadow.alpha == null ? 0.5 : s.shadow.alpha) + ")";
      ctx.shadowOffsetX = s.shadow.dx || 0; ctx.shadowOffsetY = s.shadow.dy || 0;
      ctx.shadowBlur = (s.shadow.blur || 0) * 2;   // flou Illustrator (rayon) ≈ 2 × flou canvas
      tracer();
      ctx.shadowColor = "transparent"; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
    }
    tracer();  // passe 2 : le texte net par-dessus
    ctx.restore();
    return { vide: false, taille: taille, reduit: reduit, reductionPourcent: Math.round((1 - facteur) * 1000) / 10, tient: tient };
  }

  /* API simple : ApercuPrenom.attacher({canvas, input, base, templateId, onEtat}) */
  function attacher(opts) {
    var etat = { design: null, erreur: null };
    var rendre = function () {
      if (!etat.design) return;
      var regles = etat.design.manifest.rules;
      var n = normaliser(opts.input.value, regles);
      var err = n ? valider(n, regles) : null;
      var cr = dessiner(opts.canvas, etat.design, err ? "" : n);
      if (cr && !cr.tient && !err) err = "Ce prénom est trop long pour ce design.";
      if (opts.onEtat) opts.onEtat({ prenom: n, erreur: err, rendu: cr });
    };
    var promesse = opts.manifestUrl ? chargerDepuis(opts) : charger(opts.base, opts.templateId);
    promesse.then(function (d) {
      etat.design = d; rendre();
    }).catch(function (e) {
      if (opts.onEtat) opts.onEtat({ prenom: "", erreur: "Aperçu indisponible (" + e.message + ").", rendu: null });
    });
    var timer = null, actif = true;
    var onInput = function () { clearTimeout(timer); timer = setTimeout(function () { if (actif) rendre(); }, 120); };
    opts.input.addEventListener("input", onInput);
    var rendreSiActif = rendre;
    rendre = function () { if (actif) rendreSiActif(); };
    return { rendre: rendre, etat: etat, detacher: function () { actif = false; opts.input.removeEventListener("input", onInput); } };
  }

  global.ApercuPrenom = { charger: charger, chargerDepuis: chargerDepuis, dessiner: dessiner, normaliser: normaliser, valider: valider, attacher: attacher, attacherShopify: attacher };
})(window);
